import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConnect, useAccount, useNetwork, useSwitchNetwork, useBalance } from 'wagmi';
import { supportedChains } from 'config/wagmi';

import { setNativeBalance } from 'store/entities/wallet/slice';
import { userAgentSelector, isCountryBlacklistedSelector } from 'store/entities/config/selectors';

import ConnectWalletDropdown from 'components/header/connectWallets/ConnectWalletDropdown';
import CopyToClipboard from 'elements/copyToClipboard';

import { useInstrumentPairBalance } from 'hooks';
import { redirectToCoreDownload, redirectToMetamaskDownload, renderImage } from './utils';

import { WalletNames } from 'types';

import avalancheChainIcon from 'assets/images/supportedChains/avalanche.png';
import walletIcon from 'assets/images/wallet-icon.svg';

import './ConnectButton.scss';

const ConnectButtons = () => {
  const dispatch = useDispatch();

  const userAgent = useSelector(userAgentSelector);
  const isForbiddenRegion = useSelector(isCountryBlacklistedSelector);

  const { connect, connectors, error, isLoading, pendingConnector } = useConnect();
  const { connector: activeConnector, isConnected, address } = useAccount();
  const { chain, chains } = useNetwork();
  const {
    error: chainError,
    isLoading: isChainLoading,
    pendingChainId,
    switchNetwork,
  } = useSwitchNetwork({
    chainId: supportedChains[0].id,
    throwForSwitchChainNotSupported: true,
  });
  const { data: balance } = useBalance({
    address,
    watch: true,
  });
  useInstrumentPairBalance(address);

  const isSupportedChain = useMemo(() => chains.find(ch => ch.id === chain?.id), [chain]);

  useEffect(() => {
    if (balance) {
      dispatch(setNativeBalance(balance));
    }
  }, [balance]);

  useEffect(() => {
    if (chain && switchNetwork) {
      if (!isSupportedChain) {
        switchNetwork();
      }
    }
  }, [chain, switchNetwork]);

  const renderChainButton = () => {
    if (isConnected && !isSupportedChain) {
      return <div className="connect-wallet-chain wrong-chain">{_t('Wrong Chain', 'HEADER.WRONG_CHAIN')}</div>;
    }

    return (
      <div className="connect-wallet-chain">
        <img src={avalancheChainIcon} alt="avalanche" />
        <span>{chain ? chain.name : 'Avalanche'}</span>
      </div>
    );
  };

  const selectWalletHandler = (name: string) => {
    if (isForbiddenRegion) return;

    const connector = connectors?.find(c => c.name === name);

    //@ts-ignore
    if (name === WalletNames.Core && !window?.ethereum?.coreProvider) {
      redirectToCoreDownload(userAgent);
      return;
    }

    if (name === WalletNames.Metamask && connector?.ready === false) {
      redirectToMetamaskDownload(userAgent);
      return;
    }

    if (connector) {
      connect({ connector });
    }
  };

  const connectWalletOptions = useMemo(
    () =>
      connectors.map(({ id, name }) => {
        return {
          value: id,
          label: name,
          image: renderImage(name),
        };
      }),
    [connectors]
  );

  const formattedAddress =
    isConnected && `${address?.slice(0, 5)}...${address?.substring(address.length - 5, address.length)}`.toUpperCase();

  return (
    <div className="connect-wallet-container">
      {renderChainButton()}
      {isConnected && (
        <CopyToClipboard id="copy-wallet-address" text={address} wrapperClass="copy-wallet-address">
          <div className="connect-wallet-address">
            <div className="icon-container">
              <img src={walletIcon} alt="wallet" />
            </div>
            {formattedAddress}
          </div>
        </CopyToClipboard>
      )}

      {!isConnected && (
        <ConnectWalletDropdown
          options={connectWalletOptions}
          handlerSelect={selectWalletHandler}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ConnectButtons;
